import React from 'react'
import PropTypes from 'prop-types'

import qs from 'querystring'
import MarkerClusterer from 'marker-clusterer-plus'
import { withScriptJs } from 'components/ScriptJs'
import { each, isEqual, isFunction, property, forEach } from 'lodash'

import { getIconForHighSchool } from 'routes/explore/Explore/ExploreMap/icon-helper'

const GOOGLE_QUERY_FN = (key) =>
  qs.stringify({
    v: 'weekly',
    libraries: 'drawing',
    key,
  })

export const withGoogleMapsApi = (key) => (Component) =>
  withScriptJs(`https://maps.googleapis.com/maps/api/js?${GOOGLE_QUERY_FN(key)}`)(Component)

class GoogleMap extends React.Component {
  static propTypes = {
    instanceRef: PropTypes.func,
    mapOptions: PropTypes.object,
    center: PropTypes.object.isRequired,
    zoom: PropTypes.number.isRequired,
    data: PropTypes.array.isRequired,
    dataId: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    onZoomChanged: PropTypes.func,
    onDragEnd: PropTypes.func,
    cluster: PropTypes.bool,
    clusterOptions: PropTypes.object,
    onHighSchoolSelect: PropTypes.func.isRequired,
    selectedHighSchoolId: PropTypes.string,
  }

  markers = {}

  componentDidMount() {
    if (!window.google) {
      throw new Error('window.google is not defined. Is the google maps api available on the page?')
    }
    this.map = new window.google.maps.Map(this.myRef, {
      zoom: this.props.zoom,
      center: this.props.center,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      streetViewControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
    })

    this.map.setOptions(this.props.mapOptions)
    this.cluster = new MarkerClusterer()
    this.configureCluster(this.props.clusterOptions)
    if (this.props.cluster) {
      this.activateCluster()
    }

    this.map.addListener('zoom_changed', () => {
      this.props.onZoomChanged(this.map.getZoom(), this.map.getCenter().toJSON())
    })
    this.map.addListener('dragend', () => {
      this.props.onDragEnd(this.map.getCenter().toJSON())
    })
    this.generateMarkers(this.props.data)
    this.props.instanceRef?.({
      getMarkerById: (id) => this.markers[id],
      exitStreetView: () => this.exitStreetView(),
    })
  }
  exitStreetView() {
    this.map.getStreetView().setVisible(false)
  }
  removeStaleMarkers() {
    const staleMarkerIds = Object.keys(this.markers)
    forEach(staleMarkerIds, (id) => this.markers[id].setMap(null))
    this.markers = {}
    this.cluster.clearMarkers()
  }
  generateMarkers(dataSet) {
    const newMarkers = []
    this.removeStaleMarkers()

    forEach(dataSet, (item) => {
      const idAccessor = isFunction(this.props.dataId) ? this.props.dataId : property(this.props.dataId)
      const id = idAccessor(item)

      const marker = new window.google.maps.Marker()
      if (!this.props.cluster) {
        marker.setMap(this.map)
      }

      const hsd = item.HighSchoolDetail
      marker.scoirSchool = hsd.Active
      marker.scoirId = hsd.HighSchoolId
      marker.hasScheduledVisit = hsd.HasScheduledVisit
      marker.setPosition({
        lat: hsd.Latitude,
        lng: hsd.Longitude,
      })
      marker.setTitle(hsd.SchoolName)
      const isSelectedSchool = hsd.HighSchoolId === this.props.selectedHighSchoolId
      const iconForSchool = getIconForHighSchool(hsd.Active, hsd.HasScheduledVisit, isSelectedSchool)
      marker.setIcon(iconForSchool)
      marker.addListener('click', () =>
        this.props.onHighSchoolSelect(marker.scoirId, hsd.Latitude, hsd.Longitude, false),
      )

      this.markers[id] = marker
      newMarkers.push(this.markers[id])
    })

    if (this.props.cluster) {
      this.cluster.addMarkers(newMarkers)
    }
  }
  destroyCluster() {
    this.cluster.setMap(null)
  }
  activateCluster() {
    this.cluster.setMap(this.map)
    each(Object.values(this.markers), (marker) => marker.setMap(null))
    this.cluster.addMarkers(Object.values(this.markers))
  }
  configureCluster(clusterOptions = {}) {
    this.cluster.setGridSize(clusterOptions.gridSize)
    this.cluster.setStyles(clusterOptions.styles)
    this.cluster.setTitle('this is a cluster of schools')
  }
  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.zoom, this.props.zoom)) {
      this.map.setZoom(this.props.zoom)
    }
    if (!isEqual(prevProps.data, this.props.data)) {
      this.generateMarkers(this.props.data)
    }
    if (!isEqual(prevProps.center, this.props.center)) {
      this.map.setCenter(this.props.center)
    }
    if (!isEqual(prevProps.cluster, this.props.cluster)) {
      if (this.props.cluster) {
        this.activateCluster()
      } else {
        this.destroyCluster()
      }
    }
    if (!isEqual(prevProps.clusterOptions, this.props.clusterOptions)) {
      this.configureCluster(this.props.clusterOptions)
    }
  }
  render() {
    return (
      <div
        className="new-map-comp"
        ref={(ref) => (this.myRef = ref)}
        style={{
          height: '100%',
          width: '100%',
        }}
      ></div>
    )
  }
}

export default GoogleMap
